<template>
  <BittsLoading :is-loading="isLoading">
    <div class="c-sharing-settings-tab">
      <div class="flex justify-between items-end">
        <div class="flex flex-col gap-8">
          <h2 class="font-bold text-lg">Populations shared</h2>
          <h3 class="text-neutral-text">
            We recommend sharing your Customers, Prospects, and Open
            Opportunities.
          </h3>
        </div>
        <BittsPopover v-if="canSharePopulation" placement="bottomLeft">
          <BittsButton
            type="neutral"
            variant="outline"
            size="small"
            :text="buttonText"
            class="w-fit"
            :left-icon="['far', 'plus']"
          />
          <template #content>
            <div class="max-h-[400px] w-[300px] p-8 overflow-y-auto">
              <BittsInput
                v-model="searchItem"
                placeholder="Search Populations"
                name="population_search"
                type="search"
                class="pb-8"
              />
              <div
                class="flex items-center w-full"
                v-for="option in filteredPopulationOptions"
                :key="option.value"
                @click="createDefaultDataShare(option)"
              >
                <slot name="option" :option="option">
                  <p
                    class="c-sharing-settings-tab__pop-option"
                    :class="{
                      'c-sharing-settings-tab__pop-option--solo':
                        populationOptions.length === 1,
                    }"
                  >
                    {{ option.label }}
                  </p>
                </slot>
              </div>
            </div>
            <div v-if="!filteredPopulationOptions.length" class="text-center">
              <BittsEmptyState
                :font-awesome-icon="['fak', 'search']"
                :font-awesome-icon-style="{ height: '26px', width: '26px' }"
                title="No results"
                description="Try a different search term to find fields"
                class="w-[300px] border-none"
              />
            </div>
          </template>
        </BittsPopover>
      </div>
      <div v-if="dataShares.length === 0" class="text-center text-neutral-500">
        You are not sharing any population yet.
      </div>
      <div v-else class="flex flex-col gap-8">
        <ListContainer
          :list="dataShares"
          :has-divider="
            (dataShare) =>
              !!currentPopulationToPopulation[dataShare.populationId] &&
              !dataShares[dataShares.length - 1]
          "
          :get-key="(dataShare) => dataShare.populationId"
        >
          <template #default="{ item: dataShare }">
            <SharingItem
              :data-share="dataShare"
              :create-data-share="createCustomDataShare"
              :update-data-share="updateDataShare"
              :preset-options="presetOptions[dataShare.presetParentId]"
              :single-share-presets="singleSharePresets"
              :reset-data-share="resetDataShare"
              :default-sharing="defaultDataSharesMap[dataShare.populationId]"
              :has-unsaved-changes="hasChanges"
              @show-pop-overrides-modal="
                onShowPopOverridesModalClicked(dataShare.populationId)
              "
            />
          </template>
          <template #extra="{ item: dataShare }" v-if="partnerId">
            <PopulationToPopulationSettings
              :population-id="dataShare.populationId"
              :partner-name="
                partnersStore.getPartnerOrgById(partnerId)?.name ?? ''
              "
              :partner-id="partnerId"
              :delete-override="deleteOverride"
              :current-population-to-population="currentPopulationToPopulation"
            />
          </template>
        </ListContainer>
      </div>
      <PopOverridesModal
        v-if="showPopOverridesModal && hasSharingOverrides"
        :population-id="overridePopulationId"
        @closed="onClosePopOverridesModal"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsEmptyState,
  BittsInput,
  BittsLoading,
  BittsPopover,
} from '@crossbeam/bitts';

import { computed, ref } from 'vue';

import ListContainer from '@/components/ListContainer.vue';
import PopOverridesModal from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/components/PopOverridesModal.vue';

import useAuth from '@/composables/useAuth';
import { SHARING_OVERRIDES_ENABLED } from '@/constants/feature_flags';
import { useFeatureFlagStore, usePartnersStore } from '@/stores';

import useSharingSettings from '../composables/useSharingSettings';

import PopulationToPopulationSettings from './PopulationToPopulationSettings.vue';
import SharingItem from './SharingItem.vue';

type Props = {
  partnerId?: number;
};

const { partnerId } = defineProps<Props>();

const emit = defineEmits<(e: 'bulk-update', success: boolean) => void>();

const partnersStore = usePartnersStore();

const {
  isLoading,
  dataShares,
  populationOptions,
  presetOptions,
  hasChanges,
  defaultDataSharesMap,
  singleSharePresets,
  resetDataShare,
  createDefaultDataShare,
  createCustomDataShare,
  updateDataShare,
  deleteOverride,
  bulkUpdateDataShares,
  currentPopulationToPopulation,
} = useSharingSettings(partnerId);

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));
const hasManagePops = computed(() => hasPermission('manage:populations'));

const canSharePopulation = computed(() => {
  if (!populationOptions.value.length || !hasSharingPermission.value) {
    return false;
  }
  // If there's a partner, only sharing permission is required
  // If no partner (default sharing), both sharing and population management permissions are required
  return partnerId ? true : hasManagePops.value;
});

const buttonText = computed(() => {
  return dataShares.value.length > 0
    ? 'Share another Population'
    : 'Share Population';
});

const searchItem = ref('');
const filteredPopulationOptions = computed(() => {
  if (!searchItem.value) return populationOptions.value;
  return populationOptions.value.filter((opt) =>
    opt.label.toLowerCase().includes(searchItem.value.trim().toLowerCase()),
  );
});

const ffStore = useFeatureFlagStore();
const hasSharingOverrides = computed(() =>
  ffStore.hasFeatureFlag(SHARING_OVERRIDES_ENABLED),
);
const showPopOverridesModal = ref(false);
const overridePopulationId = ref<number | null>(null);
function onClosePopOverridesModal() {
  showPopOverridesModal.value = false;
}

function onShowPopOverridesModalClicked(populationId: number) {
  overridePopulationId.value = populationId;
  showPopOverridesModal.value = true;
}

async function triggerBulkUpdate() {
  try {
    await bulkUpdateDataShares();
    emit('bulk-update', true);
    return true;
  } catch (error) {
    emit('bulk-update', false);
    return false;
  }
}

defineExpose({
  triggerBulkUpdate,
  hasChanges,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../../css/theme.css";
.c-sharing-settings-tab {
  @apply flex flex-col m-24 gap-20;
}
.c-sharing-settings-tab__pop-option {
  @apply items-center cursor-pointer px-10 py-6 w-full truncate rounded-[7px];
}
.c-sharing-settings-tab__pop-option:hover {
  @apply bg-primary-background-weak;
}
</style>
